import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Instagram, Send, Loader2, Clock } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import LegalModals from "./LegalModals";
import MapEmbed from "./MapEmbed";
import { site, whatsappLink } from "../config/site";

const inputCls =
  "w-full px-4 py-3.5 rounded-xl border border-atento-blue/15 bg-white text-charcoal placeholder:text-charcoal/35 focus:outline-none focus:ring-2 focus:ring-sage focus:border-transparent transition-all text-sm sm:text-base";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [legal, setLegal] = useState(null);

  useEffect(() => {
    const handler = (e) =>
      setForm((f) => ({ ...f, message: e.detail || f.message }));
    window.addEventListener("atento:prefill", handler);
    return () => window.removeEventListener("atento:prefill", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Debes aceptar la política de privacidad para enviar la consulta.");
      return;
    }
    setSending(true);
    try {
      const encode = (data) =>
        Object.keys(data)
          .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`)
          .join("&");
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "contacto",
          nombre: form.name.trim(),
          email: form.email.trim(),
          telefono: form.phone.trim(),
          motivo: form.message.trim(),
          privacidad: "aceptada",
          "bot-field": "",
        }),
      });
      if (!res.ok) throw new Error("netlify-form-error");
      toast.success("Consulta enviada correctamente. Te responderé lo antes posible. ¡Gracias!");
      setForm({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch (err) {
      toast.error(
        "No se pudo enviar la consulta. Inténtalo de nuevo o escríbeme por WhatsApp.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Contacto"
              title="Cuéntame qué necesitas"
              intro="El primer paso es una conversación. Escríbeme por el canal que te resulte más cómodo y te responderé personalmente."
            />
            <Reveal delay={0.08}>
              <p
                data-testid="contact-areas-text"
                className="mt-4 text-sm sm:text-base leading-relaxed text-charcoal/65 border-l-2 border-sage pl-4"
              >
                {site.areas}
              </p>
            </Reveal>

            <Reveal delay={0.15} className="mt-10 space-y-5">
              <div className="flex items-start gap-4" data-testid="contact-phone-row">
                <span className="shrink-0 w-11 h-11 rounded-full bg-sage-light flex items-center justify-center">
                  <Phone className="w-5 h-5 text-sage-dark" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="font-accent font-semibold text-atento-blue text-sm">Teléfono y WhatsApp</p>
                  <a
                    href={site.phoneHref}
                    data-testid="contact-phone-link"
                    className="text-charcoal/70 hover:text-atento-blue transition-colors text-base"
                  >
                    {site.phoneDisplay}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-email-row">
                <span className="shrink-0 w-11 h-11 rounded-full bg-sage-light flex items-center justify-center">
                  <Mail className="w-5 h-5 text-sage-dark" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="font-accent font-semibold text-atento-blue text-sm">Correo electrónico</p>
                  {site.email ? (
                    <a href={`mailto:${site.email}`} data-testid="contact-email-link" className="text-charcoal/70 hover:text-atento-blue transition-colors text-base">
                      {site.email}
                    </a>
                  ) : (
                    <p className="text-charcoal/50 text-base italic" data-testid="contact-email-pending">
                      Pendiente de añadir · mientras tanto, escríbenos por WhatsApp
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-location-row">
                <span className="shrink-0 w-11 h-11 rounded-full bg-sage-light flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-sage-dark" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="font-accent font-semibold text-atento-blue text-sm">Ubicación</p>
                  <p className="text-charcoal/70 text-base" data-testid="contact-address-text">
                    {site.address}
                  </p>
                  <p className="text-charcoal/55 text-sm">{site.city}</p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-schedule-row">
                <span className="shrink-0 w-11 h-11 rounded-full bg-sage-light flex items-center justify-center">
                  <Clock className="w-5 h-5 text-sage-dark" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="font-accent font-semibold text-atento-blue text-sm">Horario</p>
                  <p className="text-charcoal/70 text-base" data-testid="contact-schedule-text">
                    {site.schedule}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-instagram-row">
                <span className="shrink-0 w-11 h-11 rounded-full bg-sage-light flex items-center justify-center">
                  <Instagram className="w-5 h-5 text-sage-dark" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="font-accent font-semibold text-atento-blue text-sm">Instagram</p>
                  <a
                    href={site.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram-link"
                    className="text-charcoal/70 hover:text-atento-blue transition-colors text-base"
                  >
                    {site.instagramHandle}
                  </a>
                </div>
              </div>

              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="mt-4 inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1eb85a] text-white font-accent font-semibold px-7 py-4 rounded-full shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                  <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.45 9.9-9.91A9.85 9.85 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24a8.2 8.2 0 0 1 8.24 8.25c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.13-1.47-.72-1.69-.8-.23-.09-.4-.13-.56.12-.17.25-.64.8-.78.97-.15.17-.29.19-.54.06-.25-.12-1.05-.38-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.51.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.13-.56-1.35-.77-1.85-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29Z" />
                </svg>
                Escríbeme por WhatsApp
              </a>

              <MapEmbed />
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                onSubmit={submit}
                name="contacto"
                data-testid="contact-form"
                className="bg-ivory border border-atento-blue/10 rounded-3xl p-7 sm:p-10 shadow-[0_12px_44px_rgba(27,59,111,0.07)]"
              >
                <h3 className="font-serif text-2xl font-semibold text-atento-blue">
                  Formulario de contacto
                </h3>
                <p className="mt-2 text-sm text-charcoal/60 leading-relaxed">
                  Solo necesitamos tus datos de contacto y el motivo general de la consulta.
                  No es necesario incluir información detallada sobre el menor.
                </p>

                <div className="mt-7 grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block font-accent font-semibold text-xs uppercase tracking-[0.12em] text-atento-blue mb-2">
                      Nombre del adulto *
                    </label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      type="text"
                      required
                      minLength={2}
                      maxLength={120}
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Tu nombre y apellidos"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block font-accent font-semibold text-xs uppercase tracking-[0.12em] text-atento-blue mb-2">
                      Correo electrónico *
                    </label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      placeholder="tucorreo@ejemplo.com"
                      className={inputCls}
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="contact-phone" className="block font-accent font-semibold text-xs uppercase tracking-[0.12em] text-atento-blue mb-2">
                    Teléfono (opcional)
                  </label>
                  <input
                    id="contact-phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    maxLength={30}
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="600 000 000"
                    className={inputCls}
                  />
                </div>

                <div className="mt-5">
                  <label htmlFor="contact-message" className="block font-accent font-semibold text-xs uppercase tracking-[0.12em] text-atento-blue mb-2">
                    Motivo general de la consulta *
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    minLength={10}
                    maxLength={2000}
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Cuéntame brevemente qué necesitas o en qué puedo ayudarte"
                    className={`${inputCls} resize-y`}
                  />
                </div>

                <div className="mt-6 flex items-start gap-3">
                  <input
                    id="contact-consent"
                    data-testid="contact-consent-checkbox"
                    type="checkbox"
                    required
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-atento-blue/30 text-atento-blue accent-[#1B3B6F] cursor-pointer"
                  />
                  <label htmlFor="contact-consent" className="text-xs sm:text-sm leading-relaxed text-charcoal/65">
                    He leído y acepto la{" "}
                    <button
                      type="button"
                      onClick={() => setLegal("privacidad")}
                      data-testid="contact-privacy-link"
                      className="font-semibold text-atento-blue underline decoration-sage underline-offset-2 hover:decoration-atento-blue cursor-pointer"
                    >
                      política de privacidad
                    </button>
                    . Mis datos se utilizarán únicamente para responder a esta consulta. *
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-atento-blue hover:bg-atento-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-accent font-semibold px-9 py-4 rounded-full shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Enviando…
                    </>
                  ) : (
                    <>
                      Enviar consulta
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>

      <LegalModals open={legal} onClose={() => setLegal(null)} />
    </section>
  );
}
